import React, {createRef, ReactElement, useEffect, useState} from "react";
import {BaseFragment, BaseFragmentProps, BaseFragmentState} from "../shared/BaseFragment";
import {ButtonBase, Grid, IconButton, SvgIcon, Tooltip, Typography} from "@mui/material";
import {StyledBoxColumn, StyledBoxRow, StyledOrientationComponent} from "../shared/StyledComponents";
import {
  BORDER_RADIUS,
  DIVIDER_XBLACK,
  PAGE_FRAGMENT_HALF_WIDTH,
  PAGE_FRAGMENT_SM_WIDTH,
  PD_LG,
  PD_MD,
  PD_XSM
} from "../shared/dimens";
import {PeppinThreePaneFragment, PPNThreePaneFragmentListener, Tool, ToolOption} from "../peppin-shared/Components";
import {CircleOutlined, CloseOutlined, ColorLensOutlined, FlipOutlined, SquareOutlined} from "@mui/icons-material";
import {$KTM, KeyMetadatas, Orientation} from "../shared/types";
import {PARTS} from "./types";
import {App} from "./App";

enum BackgroundShape {
  NONE = "none",
  SQUARE = "square",
  CIRCLE = "circle",
}

type ShapeMetadata = {
  iconType: typeof SvgIcon,
}
const SHAPE_METADATAS = new KeyMetadatas<ShapeMetadata>([
  $KTM(BackgroundShape.NONE, {iconType: CloseOutlined}),
  $KTM(BackgroundShape.SQUARE, {iconType: SquareOutlined}),
  $KTM(BackgroundShape.CIRCLE, {iconType: CircleOutlined}),
]);

const PALETTE = [
  '#fa541c',
  '#faad14',
  '#fadb14',
  '#a0d911',
  '#52c41a',
  '#1890ff',
  '#2f54eb',
  '#722ed1',
  '#eb2f96',
  '#bfbfbf',
  '#a1a1a1',
  '#909090',
];

function BackgroundView(props: {
  initialShape: BackgroundShape,
  initialColor: string,
  onChange: (shape: BackgroundShape, color: string) => void,
}): ReactElement {
  const [shape, setShape] = useState<BackgroundShape>(props.initialShape);
  const [color, setColor] = useState<string>(props.initialColor);
  useEffect(() => {
    props.onChange(shape, color);
  }, [shape, color]);
  return <StyledBoxColumn style={{width: PAGE_FRAGMENT_HALF_WIDTH, padding: PD_LG}}>
    <Typography style={{fontWeight: "bold"}}>Shape</Typography>
    <Grid container>
      {Object.getOwnPropertyNames(BackgroundShape).map(name => {
        const s = BackgroundShape[name];
        const metadata = SHAPE_METADATAS.findMetadata(s);
        return <Grid xs={4}><ButtonBase
          onClick={() => setShape(s)}
          style={{
            boxSizing: "border-box",
            border: s === shape ? DIVIDER_XBLACK : null,
            width: "100%",
            aspectRatio: 2,
            padding: PD_XSM
          }}>
          <metadata.iconType/>
        </ButtonBase></Grid>;
      })}
    </Grid>
    <hr/>
    <Typography style={{fontWeight: "bold"}}>Color</Typography>
    <Grid container>
      {PALETTE.map(c => <Grid xs={4}><ButtonBase
        onClick={() => setColor(c)}
        style={{
          boxSizing: "border-box",
          border: c === color ? DIVIDER_XBLACK : null,
          width: "100%",
          aspectRatio: 2,
          padding: PD_XSM
        }}>
        <div style={{width: "100%", height: "100%", background: c, borderRadius: BORDER_RADIUS}}/>
      </ButtonBase></Grid>)}
    </Grid>
  </StyledBoxColumn>
}

export type MainFragmentProps = BaseFragmentProps & {}

type MainFragmentState = BaseFragmentState & {
  previewSvg: string,
  partsSelected: Map<string, string>,
  flip: boolean,
  shape: BackgroundShape,
  color: string,
}

const DEFAULT_PARTS = new Map<string, string>(
  [
    ["face", "2"],
    ["nose", "3"],
    ["mouth", "1"],
    ["eyes", "2"],
    ["eyebrows", "0"],
    ["glasses", "0"],
    ["hair", "4"],
    ["accessories", "0"],
    ["details", "0"],
    ["beard", "0"],
  ]
);

const SVGFilter = `<defs>
  <filter id="filter" x="-20%" y="-20%" width="140%" height="140%" filterUnits="objectBoundingBox" primitiveUnits="userSpaceOnUse" color-interpolation-filters="linearRGB">
    <feMorphology operator="dilate" radius="20 20" in="SourceAlpha" result="morphology"/>
    <feFlood flood-color="#ffffff" flood-opacity="1" result="flood"/>
    <feComposite in="flood" in2="morphology" operator="in" result="composite"/>
    <feMerge result="merge">
          <feMergeNode in="composite" result="mergeNode"/>
      <feMergeNode in="SourceGraphic" result="mergeNode1"/>
      </feMerge>
  </filter>
</defs>`;

export class MainFragment extends BaseFragment<MainFragmentProps, MainFragmentState> implements PPNThreePaneFragmentListener {

  private readonly paneFragment = createRef<PeppinThreePaneFragment>();

  protected onCreateState(): MainFragmentState {
    return {
      ...super.onCreateState(),
      flip: false,
      shape: BackgroundShape.NONE,
      color: PALETTE[0],
    };
  }

  setSelectedToolOptionIds(selectedToolOptionIds: Map<string, string>) {
    this.paneFragment.current.setSelectedToolOptionIds(selectedToolOptionIds);
  }

  private createToolOptions(pathTemplate: string, count: number): ToolOption[] {
    const options: ToolOption[] = []
    for (let i = 0; i < count; i++) {
      options.push({
        id: "" + i,
        image: pathTemplate.replace("{id}", "" + i),
      });
    }
    return options;
  }

  private createTool(id: string, name: string, count: number): Tool {
    return {
      id: id,
      text: name,
      image: "preview",
      toolOptions: {
        title: "Select " + name,
        options: this.createToolOptions(`/assets/part/${id}/${id}-{id}.svg`, count),
      },
      defaultToolOptionId: "0",
    };
  }

  private readonly TOOLS: Tool[] = PARTS.map(part => this.createTool(part.id, part.name, part.count));

  componentDidUpdate(prevProps: Readonly<MainFragmentProps>, prevState: Readonly<MainFragmentState>, snapshot?: any) {
    super.componentDidUpdate(prevProps, prevState, snapshot);
    if (prevState.partsSelected !== this.state.partsSelected
      || prevState.flip !== this.state.flip) {
      this.generatePreview();
    }
  }

  private onFlip() {
    this.setState({
      flip: !this.state.flip,
    });
  }

  private onBackground(event) {
    App.CONTEXT.showPopover(event.target, null, () =>
      <BackgroundView
        initialShape={this.state.shape}
        initialColor={this.state.color}
        onChange={(shape, color) => this.setState({shape, color})}/>);
  }

  onToolOptionsSelected(toolOptionIds: Map<string, string>): void {
    this.setState({
      partsSelected: toolOptionIds,
    })
  }

  onToolSelected(toolId: string): void {
  }

  private async generatePreview() {
    const groups = await Promise.all(
      PARTS.map(async (type) => {
        /* eslint-disable */
        const url = `/assets/preview/${type.id}/${this.state.partsSelected.get(type.id)}.svg`;
        const svgRaw = await fetch(url).then(res => res.text());
        return `\n<g id="peppin-avatar-maker-${type.id}" ${
          type.id === 'face' ? 'fill="#ffffff"' : ''
        } ${this.state.flip ? 'transform="scale(-1,1) translate(-1080, 0)"' : ''}>\n
      ${svgRaw.replace(/<svg.*(?=>)>/, '').replace('</svg>', '')}
    \n</g>\n`;
      }),
    );

    const previewSvg =
      `<svg viewBox="0 0 1080 1080" fill="none" xmlns="http://www.w3.org/2000/svg">
        ${SVGFilter}
      <g id="peppin-avatar-maker" filter="url(#filter)">
        ${groups.join('\n\n')}
      </g>
      </svg>`
        .trim()
        .replace(/(\n|\t)/g, '');

    this.setState({previewSvg});
  };

  private getBackgroundRadius(): string {
    switch (this.state.shape) {
      default:
      case BackgroundShape.NONE:
        return "0%";
      case BackgroundShape.SQUARE:
        return "5%";
      case BackgroundShape.CIRCLE:
        return "50%";
    }
  }

  renderContainerContent(): React.ReactElement {
    return <PeppinThreePaneFragment
      ref={this.paneFragment}
      tools={this.TOOLS}
      defaultToolOptions={DEFAULT_PARTS}
      listener={this}>
      <StyledOrientationComponent
        breakpoint={"md"}
        render={orientation => this.state.previewSvg ?
          <div style={{
            position: "relative",
            display: "flex",
            flexGrow: 1,
            alignItems: "center",
            justifyContent: "center",
            padding: "4%"
          }}>
            <div
              id="avatar-preview"
              style={{
              width: orientation === Orientation.LANDSCAPE ? "100%" : null,
              height: orientation === Orientation.LANDSCAPE ? null : "100%",
              maxWidth: PAGE_FRAGMENT_SM_WIDTH,
              maxHeight: PAGE_FRAGMENT_SM_WIDTH,
              alignSelf: "center",
              aspectRatio: 1,
              borderRadius: this.getBackgroundRadius(),
              background: this.state.shape !== BackgroundShape.NONE ? this.state.color : null,
            }}
                 dangerouslySetInnerHTML={{
                   __html: this.state.previewSvg,
                 }}
            />
            <StyledBoxRow style={{position: "absolute", top: PD_MD, right: PD_MD}}>
              <Tooltip title={"Flip"}>
                <IconButton onClick={() => this.onFlip()}>
                  <FlipOutlined/>
                </IconButton>
              </Tooltip>
              <Tooltip title={"Background"}>
                <IconButton onClick={(event) => this.onBackground(event)}>
                  <ColorLensOutlined/>
                </IconButton>
              </Tooltip>
            </StyledBoxRow>
          </div>
          : null}/>
    </PeppinThreePaneFragment>;
  }
}
